import { Getter, Setter, atom } from "jotai";
import { v4 as uuidv4 } from "uuid";
import { IntelliUpload, IntelliUploadProcessed, intelliUploadAtom } from "./intelliUpload.atoms";
import { UploadNewDocumentUseCase, ProcessDocumentsUseCase, InitiateRuleEngineUseCase } from "@/core/usecases/intelli-upload";
import { IntelliUploadRepository } from "@/infrastructure/repositories/intelli-upload";
import { IntelliUploadStatus } from "@/core/repositories/intelli-upload";
import { IntelliUploadDocument } from "@/core/entities/IntelliUpload";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { purchasersAtom } from "@/store/lookup";
import { PurchaserKeys } from "@/presentation/deduction/components/DeductionDrawer/DeductionIncomplete/context/DeductionIncompleteContext";
import { AppConfig } from "@/infrastructure/services/config";
import { DocumentMimeType } from "@/core/enums/DocumentMimeType";
import { createLogger } from "@/infrastructure/services/logger";

export const MAX_FILE_SIZE = 15 * 1024 * 1024; // 5MB
export const MAX_FILE_COUNT = 20;
export const ALLOWED_FILE_TYPES = [
  {
    mimeType: DocumentMimeType.PDF,
    extension: "pdf",
  },
  {
    mimeType: DocumentMimeType.EXCEL,
    extension: "xlsx",
  },
  {
    mimeType: DocumentMimeType.EXCEL_LEGACY,
    extension: "xls",
  },
  {
    mimeType: DocumentMimeType.CSV,
    extension: "csv",
  },
  {
    mimeType: DocumentMimeType.JPEG,
    extension: "jpg",
  },
  {
    mimeType: DocumentMimeType.PNG,
    extension: "png",
  },
  {
    mimeType: DocumentMimeType.PLAIN_TEXT,
    extension: "txt",
  },
];

const TIMEZONE = AppConfig.getInstance().TIMEZONE;
// GraphQL client
const client = GraphQlAdapter.getInstance().getClient();
// Intelli upload repository
const intelliUploadRepository = new IntelliUploadRepository(client);
const logger = createLogger("intelliUpload.actions");
export const setIntelliUploadQueueIdAtom = atom(null, (get, set, queueId: string) => {
  set(intelliUploadAtom, { ...get(intelliUploadAtom), documents: get(intelliUploadAtom).documents, queueId: queueId });
});

export const addIntelliUploadAtom = atom(null, (get, set, intelliUpload: IntelliUpload) => {
  const intelliDocs = get(intelliUploadAtom);
  if (intelliDocs.documents.length >= MAX_FILE_COUNT) {
    throw new Error("Maximum file count reached");
  }
  if (intelliUpload.file.size > MAX_FILE_SIZE) {
    throw new Error("File size exceeds maximum limit");
  }
  set(intelliUploadAtom, {
    ...get(intelliUploadAtom),
    queueId: intelliDocs.queueId,
    documents: [...intelliDocs.documents, intelliUpload],
  });
});

export const setIntelliUploadAtom = atom(null, (get, set, intelliUploads: IntelliUpload[]) => {
  const currentIntelliUploadDocs = get(intelliUploadAtom);
  set(intelliUploadAtom, {
    ...get(intelliUploadAtom),
    queueId: currentIntelliUploadDocs.queueId,
    documents: [...currentIntelliUploadDocs.documents, ...intelliUploads],
  });
});

export const updateIntelliUploadAtom = atom(null, (get, set, intelliUpload: IntelliUpload) => {
  const currentIntelliUploadDocs = get(intelliUploadAtom);
  set(intelliUploadAtom, {
    ...get(intelliUploadAtom),
    queueId: currentIntelliUploadDocs.queueId,
    documents: currentIntelliUploadDocs.documents.map(doc => (doc.key === intelliUpload.key ? intelliUpload : doc)),
  });
});

export const removeIntelliUploadAtom = atom(null, (get, set, intelliUpload: IntelliUpload) => {
  set(intelliUploadAtom, {
    ...get(intelliUploadAtom),
    queueId: get(intelliUploadAtom).queueId,
    documents: get(intelliUploadAtom).documents.filter(doc => doc.key !== intelliUpload.key),
  });
});

export const clearIntelliUploadAtom = atom(null, async (get, set): Promise<{ success: boolean; message: string } | null> => {
  const queueId = get(intelliUploadAtom).queueId;

  if (!queueId) {
    return null;
  }

  set(intelliUploadAtom, {
    ...get(intelliUploadAtom),
    queueId: null,
    isClearing: true,
    processedDocuments: [],
    documents: get(intelliUploadAtom).documents.map(doc => ({ ...doc, isDeleting: true })),
  });

  try {
    set(intelliUploadAtom, {
      ...get(intelliUploadAtom),
      isClearing: false,
      isProcessing: false,
      isProcessed: false,
      processSubmittedAt: null,
      queueId: uuidv4(),
      documents: [],
      processedDocuments: [],
      isRuleEngineInitiating: false,
      isRuleEngineTriggered: false,
    });
    return { success: true, message: "Documents cleared" };
  } catch (error) {
    logger.error("Error clearing documents", error);
    set(intelliUploadAtom, {
      ...get(intelliUploadAtom),
      isClearing: false,
      queueId: queueId,
      documents: get(intelliUploadAtom).documents.map(doc => ({ ...doc, isDeleting: false })),
    });
    return { success: false, message: "Unknown error occurred" };
  }
});

const validateIntelliUpload = async (set: Setter, get: Getter): Promise<IntelliUpload[] | IntelliUpload | null> => {
  const intelliDocuments = get(intelliUploadAtom).documents;

  const checkDocument = (document: IntelliUpload) => {
    if (document.error || document.isFailed || document.isUploading || !document.isUploaded) {
      set(intelliUploadAtom, {
        ...get(intelliUploadAtom),
        queueId: get(intelliUploadAtom).queueId,
        documents: intelliDocuments.map(doc => (doc.key === document.key ? { ...doc, validationError: "Document is invalid" } : doc)),
      });
      return false;
    }
    if (!document.documentTypeId) {
      set(intelliUploadAtom, {
        ...get(intelliUploadAtom),
        queueId: get(intelliUploadAtom).queueId,
        documents: intelliDocuments.map(doc => (doc.key === document.key ? { ...doc, validationError: "Document type is required" } : doc)),
      });
      return false;
    }

    const purchaser = document.purchaserSelected?.toUpperCase() ?? document.purchaser?.toUpperCase();
    if (!purchaser) {
      set(intelliUploadAtom, {
        ...get(intelliUploadAtom),
        queueId: get(intelliUploadAtom).queueId,
        documents: intelliDocuments.map(doc => (doc.key === document.key ? { ...doc, validationError: "Purchaser is required" } : doc)),
      });
      return false;
    }

    if (purchaser === PurchaserKeys.DOT_FOODS && document.documentType?.toUpperCase() === "BACKUP" && !document.documentSubTypeId) {
      set(intelliUploadAtom, {
        ...get(intelliUploadAtom),
        queueId: get(intelliUploadAtom).queueId,
        documents: intelliDocuments.map(doc => (doc.key === document.key ? { ...doc, validationError: "Document sub type is required" } : doc)),
      });
      return false;
    }

    set(intelliUploadAtom, {
      ...get(intelliUploadAtom),
      queueId: get(intelliUploadAtom).queueId,
      documents: intelliDocuments.map(doc => (doc.key === document.key ? { ...doc, validationError: null } : doc)),
    });

    return true;
  };

  const validDocuments = intelliDocuments.filter(doc => {
    return checkDocument(doc);
  });

  return validDocuments;
};

export const validateIntelliUploadAtom = atom(null, async (get, set) => {
  const validatedDocuments = await validateIntelliUpload(set, get);
  return validatedDocuments;
});

export const processIntelliUploadAtom = atom(null, async (get, set) => {
  const queueId = get(intelliUploadAtom).queueId;
  if (!queueId) {
    return { success: false, message: "Unknown error occurred, try again." };
  }

  const validatedDocuments = (await validateIntelliUpload(set, get)) as IntelliUpload[];

  if (!validatedDocuments) {
    return { success: false, message: "No valid documents to process" };
  }

  set(intelliUploadAtom, {
    ...get(intelliUploadAtom),
    queueId: queueId,
    isProcessing: true,
    processSubmittedAt: new Date(),
    documents: validatedDocuments.map(doc => ({ ...doc, isProcessing: true })),
  });

  try {
    const purchasersLookup = get(purchasersAtom);
    const processIntelliUploadDocumentsUseCase = new ProcessDocumentsUseCase(intelliUploadRepository);
    const { processQueue } = await processIntelliUploadDocumentsUseCase.execute({
      queueId,
      timeZone: TIMEZONE,
      documents: validatedDocuments.map(doc => {
        const purchaser = purchasersLookup.data.find(
          purchaser => purchaser.purchaserKey.toUpperCase() === (doc.purchaserSelected?.toUpperCase() ?? doc.purchaser?.toUpperCase())
        );
        if (!purchaser) {
          throw new Error("Purchaser not found");
        }
        return {
          purchaserId: purchaser.purchaserId,
          purchaserKey: purchaser.purchaserKey,
          filePath: doc.filePath!,
          fileChecksum: doc.checksumValue!,
          documentTypeId: doc.documentTypeId!,
          documentSubtypeId: doc.documentSubTypeId!,
        };
      }),
    });

    set(intelliUploadAtom, {
      ...get(intelliUploadAtom),
      isProcessing: false,
      isProcessed: true,
      documents: [],
      processedDocuments: processQueue.documents.map(doc => ({ ...doc, isProcessing: true, isProcessed: false, error: null })),
    });

    return { success: true, message: "Documents processed" };
  } catch (error) {
    logger.error("Error processing documents", error);
    set(intelliUploadAtom, {
      ...get(intelliUploadAtom),
      queueId: queueId,
      isProcessing: false,
      documents: validatedDocuments.map(doc => ({ ...doc, isProcessing: false, isProcessed: false })),
    });
    return { success: false, message: "Unknown error occurred, try again." };
  }
});

const getIntelliUploadErrorMessage = (intelliUploadDocument: IntelliUploadDocument): string | null => {
  if (intelliUploadDocument.existingDocument.hasLinkedDeductions) {
    return `This document has linked deductions. Invoice number${intelliUploadDocument.existingDocument.documents.length > 1 ? "s" : ""}: ${intelliUploadDocument.existingDocument.documents.map(doc => doc.linkedDeductions.map(deduction => deduction.claimNumber).join(", ")).join(", ")}`;
  } else if (intelliUploadDocument.existingDocument.hasLinkedRemittances) {
    return `This document has linked remittances. Check number${intelliUploadDocument.existingDocument.documents.length > 1 ? "s" : ""}: ${intelliUploadDocument.existingDocument.documents.map(doc => doc.linkedRemittances.map(remittance => remittance.checkNumber).join(", ")).join(", ")}`;
  } else if (intelliUploadDocument.existingDocument.documents.length > 0) {
    return `This document is already in the system`;
  }

  return null;
};

export const uploadIntelliUploadAtom = atom(null, async (get, set) => {
  const intelliDocuments = get(intelliUploadAtom).documents;
  if (intelliDocuments.length === 0) {
    return;
  }

  let queueId = get(intelliUploadAtom).queueId;

  if (!queueId) {
    set(intelliUploadAtom, { ...get(intelliUploadAtom), queueId: uuidv4(), documents: intelliDocuments });
    queueId = get(intelliUploadAtom).queueId;
  }

  while (true) {
    const nextDocument = get(intelliUploadAtom).documents.find(doc => !doc.error && !doc.isUploaded && !doc.isFailed && !doc.isUploading);

    if (!nextDocument) {
      break;
    }

    try {
      set(intelliUploadAtom, {
        ...get(intelliUploadAtom),
        queueId: queueId!,
        documents: get(intelliUploadAtom).documents.map(doc => (doc.key === nextDocument.key ? { ...doc, isUploading: true } : doc)),
      });

      const uploadNewDocumentUseCase = new UploadNewDocumentUseCase(intelliUploadRepository);
      const {
        data: { intelliUploadDocument },
      } = await uploadNewDocumentUseCase.execute({
        file: nextDocument.file,
        // type: nextDocument.documentType!,
        queueId: queueId!,
      });

      if (get(intelliUploadAtom).documents.some(doc => doc.checksumValue === intelliUploadDocument.checksumSHA256)) {
        set(intelliUploadAtom, {
          ...get(intelliUploadAtom),
          queueId: queueId!,
          documents: get(intelliUploadAtom).documents.map(doc =>
            doc.key === nextDocument.key
              ? {
                  ...doc,
                  isPending: false,
                  isUploading: false,
                  isFailed: true,
                  error: `Same document already exists in the queue: ${intelliUploadDocument.displayName}`,
                }
              : doc
          ),
        });
        continue;
      }

      set(intelliUploadAtom, {
        ...get(intelliUploadAtom),
        queueId: queueId!,
        documents: get(intelliUploadAtom).documents.map(doc =>
          doc.key === nextDocument.key
            ? {
                ...doc,
                isPending: false,
                isUploading: false,
                isUploaded: true,
                filePath: intelliUploadDocument.fileName,
                purchaser: intelliUploadDocument.purchaser?.toUpperCase() ?? nextDocument.purchaser,
                documentType: intelliUploadDocument.documentType ?? nextDocument.documentType,
                documentSubType: intelliUploadDocument.documentTag ?? nextDocument.documentSubType,
                checksumValue: intelliUploadDocument.checksumSHA256,
                status: IntelliUploadStatus.UPLOADED,
                error: getIntelliUploadErrorMessage(intelliUploadDocument),
              }
            : doc
        ),
      });
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = error.message;
        set(intelliUploadAtom, {
          ...get(intelliUploadAtom),
          queueId: queueId!,
          documents: get(intelliUploadAtom).documents.map(doc =>
            doc.key === nextDocument.key ? { ...doc, isPending: false, isUploading: false, isFailed: true, error: errorMessage } : doc
          ),
        });
      }
    }
  }
});

export const updateIntelliUploadProcessedAtom = atom(null, (get, set, intelliUploadProcessed: IntelliUploadProcessed) => {
  const processedDocuments = get(intelliUploadAtom).processedDocuments;
  set(intelliUploadAtom, {
    ...get(intelliUploadAtom),
    processedDocuments: processedDocuments.map(doc => (doc.documentId === intelliUploadProcessed.documentId ? intelliUploadProcessed : doc)),
  });
});

export const initiateRuleEngineAtom = atom(null, async (get, set, params?: { docs: IntelliUploadProcessed[]; processId: string }) => {
  const processId = params?.processId ?? get(intelliUploadAtom).queueId;
  const documents = params?.docs ?? get(intelliUploadAtom).processedDocuments;

  const initiateRuleEngineUseCase = new InitiateRuleEngineUseCase(intelliUploadRepository);

  if (!params?.processId) {
    set(intelliUploadAtom, {
      ...get(intelliUploadAtom),
      isRuleEngineInitiating: true,
    });
  }

  try {
    await initiateRuleEngineUseCase.execute({
      processId: processId!,
      documents: documents.map(doc => ({
        documentId: doc.documentId,
        documentType: doc.documentType,
        number: doc.documentType === "Backup" ? (doc.claimNumberInput ?? doc.claimNumber ?? "") : (doc.checkNumberInput ?? doc.checkNumber ?? ""),
      })),
    });

    if (!params?.processId) {
      set(intelliUploadAtom, {
        ...get(intelliUploadAtom),
        isRuleEngineInitiating: false,
        isRuleEngineTriggered: true,
        processSubmittedAt: null,
      });
    }

    return { success: true, message: "Rule engine initiated" };
  } catch (error) {
    logger.error("Error initiating rule engine", error);
    if (!params?.processId) {
      set(intelliUploadAtom, {
        ...get(intelliUploadAtom),
        isRuleEngineInitiating: false,
        isRuleEngineTriggered: false,
      });
    }

    return { success: false, message: "Unknown error occurred, try again." };
  }
});
